import { VA_FORM_IDS } from '@department-of-veterans-affairs/platform-forms/constants';
import {
  TRACKING_526EZ_BDD_SHOWN_SHA_INTRO,
  TRACKING_526EZ_BDD_SHOWN_SHA_UPLOAD,
  TRACKING_526EZ_BDD_SHOWN_SHA_VALIDATION_ALERT,
} from '../../../shared/constants';
import { trackAction } from '../../../shared/utils/tracking/datadogRumTracking';
import { getBddShaUploads, hasShaDocumentUploaded, isBDD } from '../bdd';

const SHA_MISMATCH_WARNING_CODES = new Set([
  'wrong_form',
  'unable_to_validate',
]);

// ──────────────────────────────────────────────
// BDD SHA session flags
// ──────────────────────────────────────────────

/**
 * Initializes BDD-specific SHA page-seen flags in sessionStorage.
 * Called for all users when the ITF banner is dismissed; internally
 * guards on isBDD so it is a no-op for non-BDD users.
 *
 * Only sets flags to 'false' on first call — preserves 'true' values
 * across Save & Finish Later resumes.
 *
 * @param {object} formData - The current form data from Redux state
 */
export const initBddShaTracking = formData => {
  try {
    if (!isBDD(formData)) {
      return;
    }

    if (!sessionStorage.getItem(TRACKING_526EZ_BDD_SHOWN_SHA_INTRO)) {
      sessionStorage.setItem(TRACKING_526EZ_BDD_SHOWN_SHA_INTRO, 'false');
    }
    if (!sessionStorage.getItem(TRACKING_526EZ_BDD_SHOWN_SHA_UPLOAD)) {
      sessionStorage.setItem(TRACKING_526EZ_BDD_SHOWN_SHA_UPLOAD, 'false');
    }
  } catch (error) {
    // Storage access blocked - silent fail
  }
};

// ──────────────────────────────────────────────
// Event: SHA page seen
// ──────────────────────────────────────────────

const PAGE_SEEN_KEYS = {
  intro: TRACKING_526EZ_BDD_SHOWN_SHA_INTRO,
  upload: TRACKING_526EZ_BDD_SHOWN_SHA_UPLOAD,
};

/**
 * Marks a SHA page as seen in sessionStorage.
 * Called when the SHA intro or upload page renders.
 *
 * @param {'intro'|'upload'} pageKey - Which page was seen
 */
export const trackShaPageSeen = pageKey => {
  const storageKey = PAGE_SEEN_KEYS[pageKey];
  if (storageKey) {
    sessionStorage.setItem(storageKey, 'true');
  }
};

// ──────────────────────────────────────────────
// Event: SHA destructive modal shown
// ──────────────────────────────────────────────

/**
 * Tracks when the user is shown the destructive modal warning about removing SHA evidence.
 * Fired from the SeparationHealthAssessment
 */
export const trackShaDestructiveModal = () => {
  trackAction('21-526EZ_bddShaDestructiveModalShown', {
    formId: VA_FORM_IDS.FORM_21_526EZ,
  });
};

// ──────────────────────────────────────────────
// Event: SHA download link clicked
// ──────────────────────────────────────────────

/**
 * Tracks when the user clicks the SHA download link.
 * Fired from the SeparationHealthAssessmentWarningAlert.
 */
export const trackShaDownloadLinkClicked = () => {
  trackAction('21-526EZ_bddShaDownloadShaLinkClicked', {
    formId: VA_FORM_IDS.FORM_21_526EZ,
  });
};

// ──────────────────────────────────────────────
// Event: SHA file upload validation result
// ──────────────────────────────────────────────

/**
 * Tracks the outcome of each SHA file upload, including OCR validation results.
 * Only fired when disability526DocumentValidationEnabled is on.
 * Fired from trackShaUploadValidation in uploadHelpers.js after each upload completes.
 *
 * @param {object} params
 * @param {boolean} params.hasValidationWarnings - true if the API returned any validation warning codes
 * @param {string[]} params.warningCodes - normalized string warning codes from the API (e.g. ['wrong_form'])
 * @param {string} params.uploaderVersion - identifier for which uploader component fired this event (e.g. 'v1')
 * @param {string} params.fileType - file extension derived from the uploaded file name
 * @param {number|undefined} params.uploadDurationMs - elapsed ms from payload creation to response parse
 */
export const trackShaFileUploadValidationResult = ({
  hasValidationWarnings,
  warningCodes,
  uploaderVersion,
  fileType,
  uploadDurationMs,
}) => {
  const properties = {
    hasValidationWarnings,
    hasWrongFormWarning: warningCodes.includes('wrong_form'),
    hasUnableToValidateWarning: warningCodes.includes('unable_to_validate'),
    warningCodes,
    uploaderVersion,
    fileType,
  };
  if (uploadDurationMs !== undefined) {
    properties.uploadDurationMs = uploadDurationMs;
  }
  trackAction('21-526EZ_shaFileUploadValidationResult', properties);
};

// ──────────────────────────────────────────────
// Event: SHA validation alert shown
// ──────────────────────────────────────────────

/**
 * Tracks the first time the SHA upload validation warning alert becomes visible.
 * Fired from UploadValidationWarning when warningCount first transitions to > 0.
 *
 * @param {object} params
 * @param {boolean} params.documentValidationEnabled - value of disability526DocumentValidationEnabled
 */
export const trackShaValidationAlertShown = ({ documentValidationEnabled }) => {
  try {
    sessionStorage.setItem(
      TRACKING_526EZ_BDD_SHOWN_SHA_VALIDATION_ALERT,
      'true',
    );
  } catch (_e) {
    // Storage access blocked - silent fail
  }
  trackAction('21-526EZ_shaValidationAlertShown', {
    formId: VA_FORM_IDS.FORM_21_526EZ,
    documentValidationEnabled,
  });
};

// ──────────────────────────────────────────────
// Event: SHA validation error corrected
// ──────────────────────────────────────────────

/**
 * Tracks when a user corrects SHA upload validation errors.
 *
 * This tracks every correction event to measure user behavior patterns when
 * responding to validation alerts.
 *
 * @param {object} params
 * @param {string} params.correctionType - type of correction:
 *   'all-warnings-cleared' or 'post-error-matching-document-uploaded'
 */
export const trackShaValidationErrorCorrected = ({ correctionType }) => {
  trackAction('21-526EZ_shaValidationErrorCorrected', {
    formId: VA_FORM_IDS.FORM_21_526EZ,
    correctionType,
  });
};
/**
 * Returns submission-scoped BDD/SHA properties used by submission orchestrators.
 * Keep this BDD/SHA-specific so submit orchestration can compose feature subsets
 * without duplicating BDD logic in a generic tracking file.
 *
 * @param {object} [formData] - The submitted form data
 * @returns {{isBdd: boolean, shaUploaded: boolean, shaFileCount: number, shownShaIntro: string, shownShaUpload: string}}
 */
export const getBddShaSubmissionProperties = (formData = {}) => {
  const isBdd = isBDD(formData);
  const shaUploaded = isBdd ? hasShaDocumentUploaded(formData) : false;
  const shaFileCount = isBdd ? getBddShaUploads(formData).length : 0;

  return {
    isBdd,
    shaUploaded,
    shaFileCount,
    shownShaIntro:
      sessionStorage.getItem(TRACKING_526EZ_BDD_SHOWN_SHA_INTRO) || 'false',
    shownShaUpload:
      sessionStorage.getItem(TRACKING_526EZ_BDD_SHOWN_SHA_UPLOAD) || 'false',
  };
};

/**
 * Fires BDD/SHA submission-only actions when the submitted form is BDD.
 * Called by the centralized submission orchestrator.
 *
 * @param {object} [formData] - The submitted form data
 */
const trackBddShaSubmissionEvents = (formData = {}) => {
  const { isBdd, shaUploaded, shaFileCount, shownShaIntro, shownShaUpload } =
    getBddShaSubmissionProperties(formData);

  if (!isBdd) {
    return;
  }

  trackAction('21-526EZ_bddShaUploadStatus', {
    formId: VA_FORM_IDS.FORM_21_526EZ,
    shaUploaded,
  });

  trackAction('21-526EZ_bddShownSeparationHealthAssessmentIntro', {
    formId: VA_FORM_IDS.FORM_21_526EZ,
    shownShaIntro,
  });

  trackAction('21-526EZ_bddShownSeparationHealthAssessmentUpload', {
    formId: VA_FORM_IDS.FORM_21_526EZ,
    shownShaUpload,
  });

  trackAction('21-526EZ_bddShaNumberOfFilesUploaded', {
    formId: VA_FORM_IDS.FORM_21_526EZ,
    shaFileCount,
  });
};

const resetBddShaSubmissionTracking = () => {
  try {
    sessionStorage.removeItem(TRACKING_526EZ_BDD_SHOWN_SHA_INTRO);
    sessionStorage.removeItem(TRACKING_526EZ_BDD_SHOWN_SHA_UPLOAD);
  } catch (error) {
    // Storage access blocked - silent fail
  }
};

// ──────────────────────────────────────────────
// BDD / SHA Submission tracking
// ──────────────────────────────────────────────
/**
 *  BDD only tracking fired on form submission to capture final BDD-related metrics:
 *  - 21-526EZ_bddShaUploadStatus
 *  - 21-526EZ_bddShownSeparationHealthAssessmentIntro
 *  - 21-526EZ_bddShownSeparationHealthAssessmentUpload
 *  - 21-526EZ_bddShaNumberOfFilesUploaded
 */

export const trackBddShaFormSubmitted = formData => {
  trackBddShaSubmissionEvents(formData);
  resetBddShaSubmissionTracking();
};

// ──────────────────────────────────────────────
// Event: hasUserUploadedSha (value sent to the API)
// ──────────────────────────────────────────────

/**
 * Records the exact hasUserUploadedSha boolean sent to the API on submit — the
 * BDD off-ramp signal computed by hasValidatedSha in submit-transformer.js.
 *
 * Unlike bddShaUploadStatus, this fires on every submit and is not gated on
 * isBdd, because the API value isn't: that is what lets us see a validated SHA
 * on a non-BDD claim and a BDD claim submitted without one. The value is passed
 * in (not recomputed) so FE analytics can never drift from what is sent.
 *
 * answeredYesToSha and shaFileCount separate the ways the value can be false:
 * answered no, answered yes with no files, or answered yes with files that all
 * failed validation.
 *
 * @param {object} params
 * @param {boolean} params.hasUserUploadedSha - the value sent to the API
 * @param {object} [params.formData] - the submitted form data
 */
export const trackHasUserUploadedSha = ({
  hasUserUploadedSha,
  formData = {},
}) => {
  trackAction('21-526EZ_hasUserUploadedSha', {
    formId: VA_FORM_IDS.FORM_21_526EZ,
    hasUserUploadedSha: hasUserUploadedSha === true,
    isBdd: isBDD(formData),
    answeredYesToSha: formData['view:hasSeparationHealthAssessment'] === true,
    shaFileCount: getBddShaUploads(formData).length,
  });
};

// ──────────────────────────────────────────────
// Helper: SHA validation alert seen check
// ──────────────────────────────────────────────

/**
 * Returns true if the SHA validation alert has been shown in this session.
 * Used by correction tracking to gate events on the user having seen the alert.
 * @returns {boolean}
 */
export const shaValidationAlertShown = () => {
  try {
    return (
      sessionStorage.getItem(TRACKING_526EZ_BDD_SHOWN_SHA_VALIDATION_ALERT) ===
      'true'
    );
  } catch (_e) {
    return false;
  }
};

// ──────────────────────────────────────────────
// Event: BDD SHA mismatch submission
// ──────────────────────────────────────────────

/**
 * Logs the claim ID (or in-progress form ID as fallback) when a BDD submission
 * includes SHA uploads with validation warnings (wrong_form / unable_to_validate).
 * Called from ConfirmationPoll after the submission status API responds.
 *
 * @param {object} params
 * @param {number|null} params.claimId - The claim ID returned after submission
 * @param {number|null} params.inProgressFormId - Fallback identifier when claimId is unavailable
 * @param {object} params.formData - The submitted form data
 */
export const trackBddShaMismatchSubmission = ({
  claimId,
  inProgressFormId,
  formData,
}) => {
  const identifier = claimId || inProgressFormId;
  if (!identifier || !isBDD(formData)) return;

  const shaUploads = getBddShaUploads(formData);
  const mismatchedUploads = shaUploads.filter(upload =>
    (upload.warnings || []).some(w => SHA_MISMATCH_WARNING_CODES.has(w)),
  );

  if (mismatchedUploads.length === 0) return;

  trackAction('21-526EZ_bddShaMismatchSubmission', {
    formId: VA_FORM_IDS.FORM_21_526EZ,
    claimId: claimId || null,
    inProgressFormId: inProgressFormId || null,
    mismatchedFileCount: mismatchedUploads.length,
    matchedFileCount: shaUploads.length - mismatchedUploads.length,
    totalShaFileCount: shaUploads.length,
  });
};
